import React, {useEffect, useState} from 'react';
import {MixModel, ProjectDataIndex} from '../../api/types.ts';
import useProjectData from '../../project_data/useProjectData.ts';
import {SceneData, SceneEntity, SceneEvents} from './types.ts';
import {v4 as uuidv4} from 'uuid';
import {Button} from 'reactstrap';
import Icon from '../shared/Icon.tsx';
import {Maximize2Icon, MoveIcon, RefreshCwIcon} from 'lucide-react';
import SceneEntityControl from './SceneEntityControl.tsx';
import SceneSettingsEditor from './SceneSettingsEditor.tsx';
import AddObjectMenu from './AddObjectMenu.tsx';
import getColorHexValue from './getColorHexValue.ts';
import sceneTypeIcon from './sceneTypeIcon.ts';
import sceneEntityLabel from './sceneEntityLabel.ts';
import {buildSceneTree} from './sceneTree.ts';
import useSceneMessaging from './useSceneMessaging.ts';
import {GizmoMode} from './sceneMessages.ts';
import {SceneFrameHandle} from './frame/SceneFrame.tsx';

interface SceneBuilderProps {
    project: MixModel;
    dataIndex: ProjectDataIndex;
    frameRef: React.RefObject<SceneFrameHandle>;
}

type SceneSettingsEntity = Extract<SceneEntity, {type: 'sceneSettings'}>;

const gizmoModes: {
    mode: GizmoMode;
    title: string;
    icon: typeof RefreshCwIcon;
}[] = [
    {mode: 'translate', title: 'Move', icon: MoveIcon},
    {mode: 'rotate', title: 'Rotate', icon: RefreshCwIcon},
    {mode: 'scale', title: 'Scale', icon: Maximize2Icon}
];

const SceneBuilder: React.FC<SceneBuilderProps> = ({
    project,
    dataIndex,
    frameRef
}) => {
    const {id} = project;
    const projectId = id!;

    const {projectData, manipulation} = useProjectData({projectId, dataIndex});
    const {mergeData} = manipulation;

    const sceneData = projectData as SceneData;

    const [selectedElementId, setSelectedElementId] = useState<string | null>(
        null
    );
    // The "Scene" item at the top of the list: shows the scene's settings.
    const [isSceneSelected, setIsSceneSelected] = useState(false);
    const [gizmoMode, setGizmoMode] = useState<GizmoMode>('translate');

    const {
        highlight,
        select,
        setGizmoMode: postGizmoMode,
        getCamera
    } = useSceneMessaging({
        frameRef,
        enabled: true,
        selectedElementId,
        gizmoMode,
        onSelected: elementId => {
            setIsSceneSelected(false);
            setSelectedElementId(elementId);
        },
        onTransform: (elementId, transform) =>
            mergeData(
                elementId,
                transform as Record<string, unknown>,
                SceneEvents.EntityModify
            )
    });

    // If the selected entity disappears (deleted locally or remotely), clear
    // selection so we don't leave a gizmo attached to a disposed mesh.
    useEffect(() => {
        if (selectedElementId && !sceneData[selectedElementId]) {
            setSelectedElementId(null);
            select(null);
        }
    }, [selectedElementId, sceneData, select]);

    const selectEntity = (elementId: string | null) => {
        setIsSceneSelected(false);
        setSelectedElementId(elementId);
        select(elementId);
    };

    const selectScene = () => {
        setIsSceneSelected(true);
        setSelectedElementId(null);
        select(null);
    };

    const changeGizmoMode = (mode: GizmoMode) => {
        setGizmoMode(mode);
        postGizmoMode(mode);
    };

    const addEntity = (entity: Omit<SceneEntity, 'elementId'>) => {
        const elementId = uuidv4();
        mergeData(elementId, {...entity, elementId}, SceneEvents.EntityAdd);
        selectEntity(elementId);
    };

    const refreshIframe = () => frameRef.current?.reload();

    const rows = buildSceneTree(sceneData);
    // There should be one settings row; if there are more, the last one wins.
    const [settingsElementId, settings] =
        Object.entries(sceneData)
            .filter(
                (entry): entry is [string, SceneSettingsEntity] =>
                    entry[1].type === 'sceneSettings'
            )
            .pop() ?? [];
    const selectedEntity = selectedElementId
        ? sceneData[selectedElementId]
        : undefined;

    return (
        <div className="scene-builder d-flex flex-column overflow-hidden flex-grow-1">
            <div className="scene-tree custom-scrollbar">
                <div
                    className={`scene-tree-item${
                        isSceneSelected ? ' selected' : ''
                    }`}
                    onClick={selectScene}
                >
                    <Icon
                        icon={sceneTypeIcon('sceneSettings')}
                        className="scene-tree-icon"
                    />
                    <span className="scene-tree-label">Scene</span>
                </div>
                {rows.length === 0 && (
                    <div className="scene-empty">
                        No objects yet. Use “Add object” below to begin.
                    </div>
                )}
                {rows.map(({elementId, entity, depth}) => (
                    <div
                        key={elementId}
                        className={`scene-tree-item${
                            selectedElementId === elementId ? ' selected' : ''
                        }`}
                        style={{paddingLeft: `${0.6 + depth * 1.1}rem`}}
                        onClick={() => selectEntity(elementId)}
                        onMouseEnter={() => highlight(elementId)}
                        onMouseLeave={() => highlight(null)}
                    >
                        <Icon
                            icon={sceneTypeIcon(entity.type)}
                            className="scene-tree-icon"
                        />
                        <span className="scene-tree-label">
                            {sceneEntityLabel(entity)}
                        </span>
                        {entity.material && entity.material.color && (
                            <span
                                className="scene-color-dot"
                                style={{
                                    backgroundColor: getColorHexValue(
                                        entity.material.color
                                    )
                                }}
                            />
                        )}
                    </div>
                ))}
            </div>

            <div className="scene-add p-2">
                <AddObjectMenu addEntity={addEntity} />
            </div>

            <div className="scene-detail custom-scrollbar flex-grow-1">
                {isSceneSelected ? (
                    <SceneSettingsEditor
                        elementId={settingsElementId}
                        settings={settings}
                        manipulation={manipulation}
                        getCamera={getCamera}
                    />
                ) : selectedEntity ? (
                    <>
                        <div
                            className="gizmo-mode btn-group btn-group-sm"
                            role="group"
                            aria-label="On-canvas controller mode"
                        >
                            {gizmoModes.map(m => (
                                <Button
                                    key={m.mode}
                                    color={
                                        gizmoMode === m.mode
                                            ? 'primary'
                                            : 'secondary'
                                    }
                                    onClick={() => changeGizmoMode(m.mode)}
                                    title={m.title}
                                    aria-label={m.title}
                                >
                                    <Icon icon={m.icon} />
                                </Button>
                            ))}
                        </div>
                        <div className="gizmo-hint">
                            Drag the handles on the object in the 3D view to{' '}
                            {gizmoMode === 'translate'
                                ? 'move'
                                : gizmoMode === 'rotate'
                                ? 'rotate'
                                : 'scale'}{' '}
                            it.
                        </div>
                        <SceneEntityControl
                            key={selectedElementId!}
                            entity={selectedEntity}
                            elementId={selectedElementId!}
                            sceneData={sceneData}
                            manipulation={manipulation}
                        />
                    </>
                ) : (
                    <div className="scene-empty">
                        Select an object to edit its properties, or add one
                        above. Select Scene for the background, start camera,
                        fog and light.
                    </div>
                )}
            </div>

            <div className="actions">
                <Button onClick={refreshIframe} color="primary">
                    Refresh
                </Button>
            </div>
        </div>
    );
};

export default SceneBuilder;
